import React, { useMemo } from "react";
import { Bookmark, CheckCircle2, Circle } from "lucide-react";
import { resolveMediaUrl } from "util/mediaUrl";
import type { UserCollection } from "lib/api";

interface Props {
  collection: UserCollection;
  onToggle: () => void;
  disabled?: boolean;
}

const PRIVACY_LABELS: Record<string, string> = {
  me: "Private",
  anonymous: "Anonymous",
  public: "Public",
  follow: "Followers",
  besteas: "Besteas",
  teams: "Parteas",
  members: "Partea members",
  admin: "Admins",
};

const CollectionItemRow: React.FC<Props> = ({ collection, onToggle, disabled = false }) => {
  const imageCdn = process.env.REACT_APP_IMAGE_CDN ?? "";
  const coverUrl = useMemo(() => {
    const mediaFiles = Array.isArray(collection.properties?.media_files)
      ? collection.properties.media_files
      : [];
    const firstMedia = [...mediaFiles].sort((left, right) => (left.order ?? 0) - (right.order ?? 0))[0];
    if (firstMedia) {
      return resolveMediaUrl(firstMedia, imageCdn);
    }
    const referenceImage =
      typeof collection.properties?.reference_link_image_url === "string"
        ? collection.properties.reference_link_image_url
        : "";
    return referenceImage;
  }, [collection.properties, imageCdn]);

  const privacyKey =
    typeof collection.properties?.privacy === "string" ? collection.properties.privacy : "me";
  const privacyLabel = PRIVACY_LABELS[privacyKey] ?? "Private";

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-2xl px-2 py-3 text-left transition-colors hover:bg-eoe-icon-hover/40 dark:hover:bg-eoe-dark-icon-hover/50 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-eoe-icon-hover/60 dark:bg-eoe-dark-icon-hover/60">
        {coverUrl ? (
          <img src={coverUrl} alt="" className="size-full object-cover" />
        ) : (
          <Bookmark className="size-6 text-eoe-text-heading dark:text-eoe-dark-text-heading" aria-hidden />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
          {collection.title || "Untitled collection"}
        </div>
        <div className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{privacyLabel}</div>
      </div>
      <div className="shrink-0 text-eoe-text-heading dark:text-eoe-dark-text-heading">
        {collection.contains_entity ? (
          <CheckCircle2 className="size-7" aria-hidden />
        ) : (
          <Circle className="size-7" aria-hidden />
        )}
      </div>
    </button>
  );
};

export default CollectionItemRow;
